import { useMutation, MutationFunction, MutationOptions } from 'react-query';
import {
    FormTemplate,
    FormTemplateResponse,
    CreateFormTemplateBody,
    UpdateFormTemplateBody,
    createFormTemplate,
    updateFormTemplate,
    deleteFormTemplate,
    duplicateFormTemplate,
    publishFormTemplate,
    desactivateFormTemplate,
    createNewVersion,
} from '../../services/apiSLTP/qhse/forms/formTemplates';

// ---------------- TYPES ----------------
type UseCreateFormTemplateMutationOptions = MutationOptions<FormTemplateResponse, Error, CreateFormTemplateBody>;
type UseUpdateFormTemplateMutationOptions = MutationOptions<FormTemplateResponse, Error, { templateId: string; data: UpdateFormTemplateBody }>;
type UseDeleteFormTemplateMutationOptions = MutationOptions<{ message: string }, Error, { templateId: string }>;
type UseDuplicateFormTemplateMutationOptions = MutationOptions<FormTemplateResponse, Error, { templateId: string; title?: string }>;
type UseCreateVersionFormTemplateMutationOptions = MutationOptions<FormTemplateResponse, Error, { templateId: string }>;
type UsePublishFormTemplateMutationOptions = MutationOptions<FormTemplateResponse, Error, { templateId: string; withNotification?: boolean }>;
type UseDesactivateFormTemplateMutationOptions = MutationOptions<FormTemplateResponse, Error, { templateId: string }>;

// ---------------- MUTATIONS ----------------
export const useCreateFormTemplateMutation = (options?: UseCreateFormTemplateMutationOptions) => {
    const mutation: MutationFunction<FormTemplateResponse, CreateFormTemplateBody> = async (data) => {
        try {
            return await createFormTemplate(data);
        } catch (error) {
            throw new Error('An unexpected error occurred during createFormTemplate');
        }
    };
    return useMutation(mutation, options);
};

export const useUpdateFormTemplateMutation = (options?: UseUpdateFormTemplateMutationOptions) => {
    const mutation: MutationFunction<FormTemplateResponse, { templateId: string; data: UpdateFormTemplateBody; file?: File }> = async ({
        templateId,
        data,
        file,
    }) => {
        try {
            return await updateFormTemplate(templateId, data, file);
        } catch (error) {
            throw new Error('An unexpected error occurred during updateFormTemplate');
        }
    };
    return useMutation(mutation, options);
};

export const useDeleteFormTemplateMutation = (options?: UseDeleteFormTemplateMutationOptions) => {
    const mutation: MutationFunction<{ message: string }, { templateId: string }> = async ({ templateId }) => {
        try {
            return await deleteFormTemplate(templateId);
        } catch (error) {
            throw new Error('An unexpected error occurred during deleteFormTemplate');
        }
    };
    return useMutation(mutation, options);
};

export const useDuplicateFormTemplateMutation = (options?: UseDuplicateFormTemplateMutationOptions) => {
    const mutation: MutationFunction<FormTemplateResponse, { templateId: string; title?: string }> = async ({ templateId, title }) => {
        try {
            return await duplicateFormTemplate(templateId, title);
        } catch (error) {
            throw new Error('An unexpected error occurred during duplicateFormTemplate');
        }
    };
    return useMutation(mutation, options);
};

export const useCreateVersionFormTemplateMutation = (options?: UseCreateVersionFormTemplateMutationOptions) => {
    const mutation: MutationFunction<FormTemplateResponse, { templateId: string }> = async ({ templateId }) => {
        try {
            return await createNewVersion(templateId);
        } catch (error) {
            throw new Error('An unexpected error occurred during createNewVersion');
        }
    };
    return useMutation(mutation, options);
};

export const usePublishFormTemplateMutation = (options?: UsePublishFormTemplateMutationOptions) => {
    const mutation: MutationFunction<FormTemplateResponse, { templateId: string; withNotification?: boolean }> = async ({
        templateId,
        withNotification,
    }) => {
        try {
            return await publishFormTemplate(templateId, withNotification);
        } catch (error) {
            throw new Error('An unexpected error occurred during publishFormTemplate');
        }
    };
    return useMutation(mutation, options);
};

export const useDesactivateFormTemplateMutation = (options?: UseDesactivateFormTemplateMutationOptions) => {
    const mutation: MutationFunction<FormTemplateResponse, { templateId: string }> = async ({ templateId }) => {
        try {
            return await desactivateFormTemplate(templateId);
        } catch (error) {
            throw new Error('An unexpected error occurred during deactivateFormTemplate');
        }
    };
    return useMutation(mutation, options);
};
